import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import InsurancePolicyTimelineRepository, {
  EMPTY_TIMELINE
} from '@/repositories/InsurancePolicyTimelineRepository';
import { Button } from '@/ui';

import TimelineControls from './components/TimelineControls';
import TimelineRow from './components/TimelineRow';
import useTimelineParams from './hooks/useTimelineParams';
import { SENTINEL_POLICY_LABELS, formatPolicyLabel } from './utils';

const InsurancePolicyTimeline: React.FC = () => {
  const { personId } = useParams<{ personId: string }>();
  const repository = InsurancePolicyTimelineRepository();

  const {
    dateRange,
    selectedPolicyId,
    focusedEventId,
    sortOrder,
    selectedEventTypes,
    hasActiveFilters,
    setDateRange,
    setSelectedPolicyId,
    setSortOrder,
    setSelectedEventTypes,
    setFocusedEventId,
    resetFilters
  } = useTimelineParams();

  const { data: timeline = EMPTY_TIMELINE, isLoading } = useQuery({
    queryKey: ['insurancePolicyTimeline', personId, dateRange],
    queryFn: () =>
      repository.getTimelineForPerson(personId!, {
        start_date: dateRange.start,
        end_date: dateRange.end
      }),
    enabled: !!personId
  });

  const events = timeline.events;

  const filteredEvents = useMemo(() => {
    let result = events;

    // Filter by policy if selected
    if (selectedPolicyId) {
      result = result.filter((event) => event.policy_id === selectedPolicyId);
    }

    // Filter by event types
    result = result.filter((event) => selectedEventTypes.has(event.event_type));

    return result;
  }, [events, selectedPolicyId, selectedEventTypes]);

  const policyOptions = useMemo(() => {
    const fromRoster = timeline.policies
      .map((policy) => ({
        value: policy.id,
        label: formatPolicyLabel(policy, timeline.policies)
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const sentinels = Object.entries(SENTINEL_POLICY_LABELS)
      .filter(([policyId]) => events.some((event) => event.policy_id === policyId))
      .map(([policyId, label]) => ({ value: policyId, label }));

    return [...fromRoster, ...sentinels];
  }, [timeline.policies, events]);

  return (
    <div className="flex h-full flex-col">
      <div className="mx-auto w-full max-w-4xl">
        <PageHeader headingText="Billing & VOB Timeline" />

        <TimelineControls
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          policyOptions={policyOptions}
          selectedPolicyId={selectedPolicyId}
          onPolicyChange={setSelectedPolicyId}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
          selectedEventTypes={selectedEventTypes}
          onEventTypesChange={setSelectedEventTypes}
          eventCounts={
            isLoading ? undefined : { shown: filteredEvents.length, total: events.length }
          }
          hasActiveFilters={hasActiveFilters}
          onResetFilters={resetFilters}
        />
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-gray-500">Loading timeline...</div>
          </div>
        ) : events.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-gray-500">No policy events found</div>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <div className="text-gray-500">No events match these filters</div>
            {hasActiveFilters && (
              <Button color="neutral" onClick={resetFilters} size="sm" variant="outline">
                Reset filters
              </Button>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-4xl p-6">
            <TimelineRow
              events={filteredEvents}
              policies={timeline.policies}
              sortOrder={sortOrder}
              focusedEventId={focusedEventId}
              onFocusEvent={setFocusedEventId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InsurancePolicyTimeline;
